import type { ClientClassificationInput } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPublicClientClassificationPreviousAnswer,
  getPublicClientClassificationValue,
  savePublicClientClassification,
} from "@/lib/api/public-flow/client-classification";
import { useSessionToken } from "@/lib/context/public-session-context";
import { invalidatePublicSuitabilityQueries } from "./use-goals";
import { useInvalidatePublicDynamicSync } from "./use-portfolios";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

const keys = {
  value: ["publicFlow", "client-classification", "flow-instance-value"] as const,
  previousAnswer: ["publicFlow", "client-classification", "previous-answer"] as const,
};

export function usePublicClientClassificationValue() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.value,
    queryFn: () => getPublicClientClassificationValue(token),
    enabled: !!token,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function usePublicClientClassificationPreviousAnswer() {
  const token = useSessionToken();
  const { data, isLoading } = useQuery({
    queryKey: keys.previousAnswer,
    queryFn: () => getPublicClientClassificationPreviousAnswer(token),
    enabled: !!token,
  });
  return { previousAnswer: data ?? null, isLoading };
}

export function useSavePublicClientClassification() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  return useMutation({
    mutationFn: (input: ClientClassificationInput) => savePublicClientClassification(token, input),
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(keys.value, {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // QL-584 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      // QL-10 — professional / eligible-counterparty classification bypasses
      // the suitability rules, so verdicts must refetch. Mirrors the advisor twin.
      invalidatePublicSuitabilityQueries(queryClient);
    },
  });
}
